<template>
    <div :class="[outerClass, colSize, { 'form-field-disabled': isFormDisabled }]">
        <label class="block"
               :class="{ 'required-label': hasRequiredAttr, [labelClass]: true }"
               :style="labelStyle">{{
                __(label)
            }}</label>
        <InputNumber :mode="mode"
                     :currency="currency"
                     :locale="locale"
                     :disabled="isFormDisabled"
                     :min="min"
                     :max="max"
                     :step="step"
                     :showButtons="showButtons"
                     :minFractionDigits="minFractionDigits"
                     :maxFractionDigits="maxFractionDigits"
                     :prefix="prefix"
                     :suffix="suffix"
                     :allowEmpty="allowEmpty"
                     :useGrouping="useGrouping"
                     :inputStyle="{ 'text-align': 'right', ...inputStyle, 'min-width': '10px' }"
                     :inputClass="inputClass"
                     autocomplete="off"
                     ref="inputNumberRef"
                     class="w-full rounded-md border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-black-500/50"
                     :class="{ 'required-field': hasRequiredAttr }"
                     @update:modelValue="update"
                     @input="input"
                     @focus="focus"
                     @blur="blur"
                     v-focus="isFocus"
                     v-model="parseField"
                     :unstyled="false" />
    </div>
</template>

<script setup>
import { ref, computed, onMounted, nextTick } from 'vue';
import InputNumber from 'primevue/inputnumber';
import { __ } from '@pages/Helper/utils.js'

const props = defineProps({
    fieldName: {
        type: String,
        required: true,
    },
    validation: {
        type: [Array, String],
        default: null,
    },
    label: {
        type: String,
        default: null,
    },
    size: {
        type: String,
        default: null
    },
    isFocus: {
        type: Boolean,
        default: false
    },
    isFormDisabled: {
        type: Boolean,
        default: false
    },
    mode: {
        type: String,
        required: false
    },
    min: {
        type: Number,
        default: null
    },
    max: {
        type: Number,
        default: null
    },
    step: {
        type: Number,
        default: 1
    },
    showButtons: {
        type: Boolean,
        default: false
    },
    minFractionDigits: {
        type: Number,
        default: null
    },
    maxFractionDigits: {
        type: Number,
        default: null
    },

    currency: {
        type: String,
        required: false
    },
    locale: {
        type: String,
        required: false
    },
    inputStyle: {
        type: [Object],
        default: null
    },
    inputClass: {
        type: String,
        default: null
    },
    labelClass: {
        type: String,
        default: null
    },
    labelStyle: {
        type: [Object, String],
        default: null
    },
    prefix: {
        type: String,
        default: null
    },
    suffix: {
        type: String,
        default: null
    },
    useGrouping: {
        type: Boolean,
        default: true
    },
    allowEmpty: {
        type: Boolean,
        default: true
    },
    inRepeater: {
        type: Boolean,
        default: false
    },
    update: {
        type: Function,
        required: false,
        default: () => { }
    },
    input: {
        type: Function,
        required: false
    },
    focus: {
        type: Function,
        required: false
    },
    blur: {
        type: Function,
        required: false
    },

    default: {
        type: Number,
        default: 0
    }
});

const field = defineModel('field');

const validation = ref(Array.isArray(props.validation) ? props.validation : [props.validation]);
const colSize = props?.size ? 'col-' + props.size : 'col';
const hasRequiredAttr = validation.value.includes('required');
const inputNumberRef = ref(null);
const outerClass = ref('field');

const parseField = computed({
    get: () => {
        if (field.value !== null && field.value !== undefined && field.value !== '') {
            return Number(field.value);
        } else {
            if (props.allowEmpty) {
                return null;
            }
            field.value = props.default
            return props.default
        }
    },
    set: newValue => field.value = newValue
});

onMounted(async () => {
    if (props.isFocus) {
        await nextTick()
        inputNumberRef.value.$el.children[0]?.focus();
    }
    // Znajdź natywny input i ustaw atrybut name
    const input = inputNumberRef.value?.$el?.querySelector('input');
    if (input && props.fieldName) {
        input.setAttribute('name', props.fieldName);
        input.setAttribute('validation', validation.value.join(','));
    }
});

if (props.inRepeater) {
    outerClass.value = null;
}

</script>

<style lang="scss" scoped>
:deep(.p-inputnumber-input) {
    margin-right: 5px !important;
}

.form-field-disabled {

    // Nadajemy szare tło na główny kontener InputNumber (obejmujący ew. przyciski i input)
    :deep([data-pc-name="inputnumber"]) {
        background-color: #f5f6f8 !important;

        // Ustawiamy tło samego pola tekstowego na przezroczyste
        input {
            background-color: transparent !important;
        }
    }
}
</style>
